import ButtonComponent, { IconSize } from '../button/button/button.component';
import LinkComponent, { LinkComponentLink } from '../link/link.component';
import React, { ReactElement, memo } from 'react';
import { useSessionContent, useVisitorSession } from '@spa/lib/react/context/VisitorSessionContext';
import { ButtonType } from '../button/types/ButtonType';
import ConfigurableCmsValidity from '@spa/top-level-components/cms-page/metadata/configurable/configurable-cms-validity.component';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import classNames from 'clsx';
import styles from './overhead.component.module.scss';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';

export interface Props {
  readonly text: string | null;
  readonly link: LinkComponentLink | null;
  readonly backgroundColor?: string | null;
  readonly editorMode: boolean;
  readonly dateTo: number | null;
  readonly dateFrom: number | null;
}

function OverheadComponent({
  text,
  link,
  backgroundColor,
  editorMode,
  dateTo,
  dateFrom
}: Props): ReactElement {
  const visitorSession = useVisitorSession();
  const content = useSessionContent();
  const showBanner = content.showOverheadComponent;
  const color: string = backgroundColor ?? 'black';

  const overheadClass = classNames({
    [styles.overhead]: true,
    [styles['overhead--black']]: color === 'black',
    [styles['overhead--main']]: color === 'orange',
    [styles['overhead--pink']]: color === 'pink',
    [styles['overhead--green']]: color === 'green'
  });

  const { formatMessage }: NamespacedIntl = useNamespacedIntl('overhead');

  const closeOverhead = (): void => {
    visitorSession.setContent({ ...content, showOverheadComponent: false }, true);
  };

  const textContent: ReactElement = (
    <div className={overheadClass}>
      <div className={styles['text']}>{text}</div>
      <div className={styles['overhead__close-btn']}>
        <ButtonComponent
          buttonType={ButtonType.AS_TEXT}
          iconLeft={{
            src: '/assets/icons/close-icon.svg',
            alt: formatMessage('closeOverhead'),
            width: 5,
            height: 5,
            unoptimized: true
          }}
          iconSize={IconSize.SMALL}
          onClick={closeOverhead}
          aria-label={formatMessage('closeOverhead')}
        />
      </div>
    </div>
  );

  return (
    <>
      {editorMode && <ConfigurableCmsValidity dateFrom={dateFrom} dateTo={dateTo} />}
      {showBanner &&
        (link ? <LinkComponent link={link}>{textContent}</LinkComponent> : textContent)}
    </>
  );
}

export default memo(OverheadComponent);
